import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';

interface RegistrationTrendData {
  date: string;
  total: number;
  aspirants: number;
  mentors: number;
}

interface RegistrationTrendChartProps {
  data: RegistrationTrendData[];
  loading?: boolean;
}

const RegistrationTrendChart: React.FC<RegistrationTrendChartProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="h-80 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading chart data...</p>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="h-80 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">📊</div>
          <p className="text-gray-600">No registration data available</p>
          <p className="text-sm text-gray-500 mt-2">
            Registration data will appear here once users start registering
          </p>
        </div>
      </div>
    );
  }

  // Format the data for better display based on date format
  const formattedData = data.map(item => {
    let formattedDate = item.date;
    
    // Handle different date formats from backend
    if (item.date.includes('-')) {
      const parts = item.date.split('-');
      if (parts.length === 2) {
        // YYYY-MM format (monthly)
        formattedDate = new Date(item.date + '-01').toLocaleDateString('en-US', { 
          month: 'short', 
          year: 'numeric' 
        });
      } else if (parts.length === 3) {
        // YYYY-MM-DD format (daily)
        formattedDate = new Date(item.date).toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        });
      }
    } else if (item.date.includes('Q')) {
      // YYYY-QN format (quarterly)
      formattedDate = item.date;
    } else if (item.date.includes('W')) {
      // YYYY-WNN format (weekly) - convert to week number display
      const [year, week] = item.date.split('-W');
      formattedDate = `Week ${week}, ${year}`;
    }
    
    return {
      ...item,
      date: formattedDate
    };
  });

  // Determine if we should show line chart or bar chart based on data points
  const shouldUseBarChart = data.length <= 6;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="h-80 w-full">
      <ResponsiveContainer width="100%" height="100%">
        {shouldUseBarChart ? (
          <BarChart data={formattedData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="date" 
              stroke="#666"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            <YAxis 
              stroke="#666"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `${value}`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar 
              dataKey="total" 
              fill="#3b82f6" 
              name="Total Users"
              radius={[4, 4, 0, 0]}
            />
            <Bar 
              dataKey="aspirants" 
              fill="#10b981" 
              name="Aspirants"
              radius={[4, 4, 0, 0]}
            />
            <Bar 
              dataKey="mentors" 
              fill="#f59e0b" 
              name="Mentors"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        ) : (
          <LineChart data={formattedData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="date" 
              stroke="#666"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            <YAxis 
              stroke="#666"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `${value}`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="total" 
              stroke="#3b82f6" 
              strokeWidth={3}
              name="Total Users"
              dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2 }}
            />
            <Line 
              type="monotone" 
              dataKey="aspirants" 
              stroke="#10b981" 
              strokeWidth={3}
              name="Aspirants"
              dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2 }}
            />
            <Line 
              type="monotone" 
              dataKey="mentors" 
              stroke="#f59e0b" 
              strokeWidth={3}
              name="Mentors"
              dot={{ fill: '#f59e0b', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: '#f59e0b', strokeWidth: 2 }}
            />
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

export default RegistrationTrendChart; 